import {
	PUBLIC_ENV,
	PUBLIC_PIWIKPRO_GOAL_ID_GET_STARTED,
	PUBLIC_PIWIKPRO_GOAL_ID_LEAD,
	PUBLIC_PIWIKPRO_GOAL_ID_ONLINE_ESTIMATE_CREATED,
	PUBLIC_PIWIKPRO_GOAL_ID_ONLINE_ESTIMATE_ORDER_INITIATED,
	PUBLIC_PIWIKPRO_GOAL_ID_ONLINE_ESTIMATE_SAVED,
	PUBLIC_PIWIKPRO_GOAL_ID_SIGNUP,
	PUBLIC_PIWIKPRO_INSTANCE_NAME,
	PUBLIC_PIWIKPRO_SITE_ID,
	PUBLIC_PIWIKPRO_VISITOR_ID_COOKIE_NAME,
} from '$env/static/public';
import * as Sentry from '@sentry/sveltekit';

const piwikProUrl = new URL(
	`https://${PUBLIC_PIWIKPRO_INSTANCE_NAME}.piwik.pro/ppms.php`,
);
export const goalMap = new Map([
	[
		'lead_form_submitted',
		{
			goalId: PUBLIC_PIWIKPRO_GOAL_ID_LEAD,
			actionName: 'Lead Form',
		},
	],
	[
		'estimate_saved',
		{
			goalId: PUBLIC_PIWIKPRO_GOAL_ID_ONLINE_ESTIMATE_SAVED,
			actionName: 'selfServe-saveEstimate',
		},
	],
	[
		'estimate_created',
		{
			goalId: PUBLIC_PIWIKPRO_GOAL_ID_ONLINE_ESTIMATE_CREATED,
			actionName: 'selfServe-getEstimate',
		},
	],
	[
		'online_estimate_initiated_order',
		{
			goalId: PUBLIC_PIWIKPRO_GOAL_ID_ONLINE_ESTIMATE_ORDER_INITIATED,
			actionName: 'selfServe-depositPaid',
		},
	],
	[
		'welcome_discount',
		{
			goalId: PUBLIC_PIWIKPRO_GOAL_ID_SIGNUP,
			actionName: 'selfServe-addWelcomeDiscount',
		},
	],
	[
		'get_started',
		{
			goalId: PUBLIC_PIWIKPRO_GOAL_ID_GET_STARTED,
			actionName: 'selfServe-getStarted',
		},
	],
]);

export const client = {
	trackGoal: clientTrackGoal,
	setUserId: clientSetUserId,
};
export function clientTrackGoal(goalName) {
	const { goalId } = goalMap.get(goalName);
	if (!goalId) {
		throw new Error(`Goal ${goalName} not found`);
	}
	window._paq = window._paq || [];
	window._paq.push(['trackGoal', goalId]);
}

export function clientSetUserId(email) {
	window._paq = window._paq || [];
	window._paq.push(['setUserId', email]);
}

export async function serverSendEvent({
	eventName,
	url,
	ipAddress,
	userAgent,
	cookies,
	data,
}) {
	let result = await serverTrackGoal({
		goalName: eventName,
		url,
		ipAddress,
		userAgent,
		visitorId: cookies.get(PUBLIC_PIWIKPRO_VISITOR_ID_COOKIE_NAME),
		experiment: cookies.get('indow_experimentGroup'),
		email: data?.email,
		value: data?.value,
	});
	return result;
}

export async function serverTrackGoal({
	goalName,
	url,
	ipAddress,
	userAgent,
	email,
	value,
	experiment,
	visitorId,
	sendEmail = false,
}) {
	const { goalId, actionName } = goalMap.get(goalName);
	if (!goalId) {
		throw new Error(`Goal ${goalName} not found`);
	}
	if (!visitorId) {
		console.error('piwik pro visitor id missing');
		Sentry.captureException(new Error('piwik pro visitor id missing'), {
			extra: {
				goalName,
				url,
				ipAddress,
				userAgent,
				email,
				value,
				experiment,
				visitorId,
			},
		});
	}
	let requests = [];
	let params = new URLSearchParams({
		cdt: new Date().toISOString().slice(0, 19).replace('T', ' '),
		idsite: PUBLIC_PIWIKPRO_SITE_ID,
		idgoal: goalId,
		rec: 1,
		url: url,
		ua:
			userAgent ??
			'Mozilla/5.0 (Windows NT 10.0; Win64; x64) AppleWebKit/537.36 (KHTML, like Gecko) Chrome/114.0.0.0 Safari/537.36',
	});
	if (ipAddress) {
		params.append('cip', ipAddress);
	}

	// don't send email by default because piwik doesn't connect identified users to their previous sessions
	if (email && sendEmail) {
		params.append('uid', email);
	}
	if (value) {
		params.append('revenue', value);
	}
	if (actionName) {
		params.append('action_name', actionName);
	}
	if (experiment) {
		params.append('dimension2', experiment);
	}
	if (visitorId) {
		if (visitorId.includes('.')) {
			visitorId = visitorId.substring(0, visitorId.indexOf('.'));
		}
		params.append('_id', visitorId);
	}

	requests.push(`?${params.toString()}`);

	if (requests.length === 0) {
		return 'no requests to send';
	}

	const body = JSON.stringify({ requests });
	if (PUBLIC_ENV !== 'production') {
		console.log(
			'piwik pro requests not sent in non-production environment',
			body,
		);
		return 'piwik pro requests not sent in non-production environment';
	}

	let response;
	try {
		response = await fetch(piwikProUrl, {
			method: 'POST',
			headers: {
				'Content-Type': 'application/json',
			},
			body: body,
		});
	} catch (error) {
		console.error('Error sending Piwik Pro request:', error);
		Sentry.captureException(error, {
			extra: {
				body,
			},
		});
	}
	let responseBody;
	try {
		responseBody = await response.text();
	} catch (error) {
		console.error('Error parsing Piwik Pro response:', error);
		Sentry.captureException(error, {
			extra: {
				body,
			},
		});
	}
	let responseText = `piwik pro response: ${response.status} ${response.statusText} ${responseBody}`;
	if (PUBLIC_ENV !== 'production') {
		console.log(responseText);
	}
	return responseText;
}
